import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AUTOMATION_STEP_TYPES,
  COMPLIANCE_LANE_LABELS,
  COMPLIANCE_LANES,
  type ComplianceLane,
  type ComplianceSettings,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { Project } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import settingsStyles from '@/pages/SettingsPage.module.css';
import styles from '@/pages/ProjectsPage.module.css';

type Props = {
  projectId: string;
};

export function ComplianceSettingsPanel({ projectId }: Props) {
  const qc = useQueryClient();
  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => apiFetch<{ project: Project }>(`/api/projects/${projectId}`),
  });

  const settings = (projectQ.data?.project as Project & { compliance_settings?: ComplianceSettings })
    ?.compliance_settings;

  const [lane, setLane] = useState<ComplianceLane | ''>('');
  const [allowedSteps, setAllowedSteps] = useState<string[]>([]);

  useEffect(() => {
    if (!settings) return;
    setLane(settings.compliance_lane ?? '');
    setAllowedSteps(settings.allowed_automation_steps ?? [...AUTOMATION_STEP_TYPES]);
  }, [projectQ.data?.project?.updated_at, settings]);

  const saveM = useMutation({
    mutationFn: () =>
      apiFetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          compliance_settings: {
            compliance_lane: lane || undefined,
            allowed_automation_steps: allowedSteps.length ? allowedSteps : undefined,
          },
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  if (projectQ.isLoading) return <LoadingState />;

  return (
    <form
      className={styles.formGrid}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        saveM.mutate();
      }}
    >
      <Field label="Compliance lane">
        <select
          className={styles.filterInput}
          value={lane}
          onChange={(e) => setLane(e.target.value as ComplianceLane | '')}
        >
          <option value="">— standaard (cold B2B guardrails) —</option>
          {COMPLIANCE_LANES.map((l) => (
            <option key={l} value={l}>
              {COMPLIANCE_LANE_LABELS[l]}
            </option>
          ))}
        </select>
      </Field>
      {lane === 'opt_in_only' ? (
        <p className={settingsStyles.hint}>
          Waarschuwing: cold outreach-campagnes horen niet bij opt-in lane. Enroll policies en
          suppressions blijven leidend.
        </p>
      ) : null}

      <Field label="Toegestane automation steps">
        <div>
          {AUTOMATION_STEP_TYPES.map((st) => (
            <label key={st} className={settingsStyles.checkRow}>
              <input
                type="checkbox"
                checked={allowedSteps.includes(st)}
                onChange={(e) => {
                  setAllowedSteps((prev) =>
                    e.target.checked ? [...prev, st] : prev.filter((x) => x !== st),
                  );
                }}
              />{' '}
              {st}
            </label>
          ))}
        </div>
      </Field>

      <Button type="submit" disabled={saveM.isPending}>
        Opslaan
      </Button>
    </form>
  );
}
